import React, { useEffect } from 'react';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import {
  TransactionEntityList,
  NewTransactionEntitySidebar,
  useTransactionEntities,
} from '../components/TransactionEntity';
import { Button } from '../components/ui/Button';

const TransactionEntityPage: React.FC = () => {
  const { setNavLinks } = useNavLinksContext();

  // All state and handlers come from the hook
  const {
    // Data
    entities,
    pagination,

    // Loading
    isLoading,
    isDeleting,

    // Filter
    filter,
    handleFilterChange,

    // Sidebar
    sidebarState,
    openSidebarForNew,
    openSidebarForEdit,
    closeSidebar,
    handleEntityCreatedOrUpdated,

    // Delete modal
    deleteModalState,
    openDeleteModal,
    closeDeleteModal,
    handleConfirmDelete,

    // Entity click
    handleEntityClick,
  } = useTransactionEntities();

  // Set navigation links
  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/records', label: 'Records' },
      ],
    });
  }, [setNavLinks]);

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar pl-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="">
        {/* Two-column layout for desktop, stacked for mobile */}
        <div className="flex flex-col lg:flex-row gap-2">
          {/* Left column: Entity list */}
          <div
            className={`transition-all duration-300 ease-out ${
              sidebarState.isOpen ? 'lg:w-3/5' : 'w-full'
            }`}
          >
            <TransactionEntityList
              transactionEntities={entities}
              loading={isLoading}
              filter={filter}
              onFilterChange={handleFilterChange}
              onEdit={openSidebarForEdit}
              onDelete={openDeleteModal}
              onNewEntity={openSidebarForNew}
              onEntityClick={handleEntityClick}
              pagination={pagination}
            />

            {/* Help Section */}
            <div className="mt-4 bg-light rounded-lg p-4 sm:p-6">
              <h4 className="text-lg mb-3">Transaction Entity Guide</h4>
              <div className="space-y-3 text-sm text-grey_dark">
                <div>
                  <h5 className="font-medium text-dark mb-1">Actions</h5>
                  <p>
                    The type of transaction (e.g., Purchase, Sale, Expense, Income). These describe
                    what happened in a transaction.
                  </p>
                </div>
                <div>
                  <h5 className="font-medium text-dark mb-1">Objects</h5>
                  <p>
                    The items or services being transacted (e.g., Office Supplies, Consulting
                    Services). Can be used as direct or indirect objects.
                  </p>
                </div>
                <div>
                  <h5 className="font-medium text-dark mb-1">Payment Media</h5>
                  <p>
                    How transactions are paid (e.g., Credit Card, Bank Transfer, Cash). These
                    represent payment methods.
                  </p>
                </div>
                <div>
                  <h5 className="font-medium text-dark mb-1">KPI Linking</h5>
                  <p>
                    When enabled, transactions using this entity will contribute to KPI calculations
                    in your dashboard.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right column: Entity sidebar */}
          <div
            className={`transition-all duration-300 ease-out ${
              sidebarState.isOpen ? 'lg:w-2/5' : 'lg:w-0 hidden lg:block'
            }`}
          >
            {sidebarState.isOpen && (
              <NewTransactionEntitySidebar
                isOpen={sidebarState.isOpen}
                onClose={closeSidebar}
                onSelectEntity={handleEntityCreatedOrUpdated}
                entityToEdit={sidebarState.entityToEdit}
                isEditMode={sidebarState.isEditMode}
              />
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalState.isOpen && deleteModalState.entity && (
        <>
          <div
            className="fixed inset-0 bg-gray-700 bg-opacity-50 z-40"
            onClick={closeDeleteModal}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-light rounded-lg shadow-lg w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium mb-4">Delete Entity</h3>
              <p className="text-grey_dark mb-6">
                Are you sure you want to delete "{deleteModalState.entity.transaction_entity_name}"?
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  onClick={closeDeleteModal}
                  variant="google"
                  className="text-grey_dark"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  variant="filled-primary"
                  className="bg-danger hover:bg-danger_dark border-danger"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TransactionEntityPage;
